// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import ObjectUtils from 'core/util/ObjectUtils';
import AwsSystem from 'aws/AwsSystem';
import SystemBus from 'core/entities/SystemBus';
import AudioContext from 'core/sound/AudioContext';

let theSpeechManager = null;

/**
 * @typedef {Object} SpeechManagerConfig
 * @property {string} voiceId
 * @property {string} body
 * @property {string} voiceEngine
 */

/**
 * A manager class which contains cache for audioStream/SpeechMarks for existing speechs and handles any network calls
 */
class SpeechManager {
  constructor() {
    // The SpeechManager requires the AwsSystem SDK_READY_TO_START signal before it can start. If _ready is false, speech
    // will not start. This could be because the AWS SDK is loading, but it could also be because there is
    // no cognito Pool Id. _sceneHasCognitoPoolId will be false if this is the case.
    // _ready implies that _sceneHasCognitoPoolId is true
    this._ready = false;

    this._configQueue = [];
    this._cachedConfig = [];
    this._speechCache = {};
    this._awsSdkVersion = null;

    this._onAwsSdkReadyToStart = () => {
      this._ready = true;
      this._awsSdkVersion = parseFloat(AWS.VERSION.match(/(\d+\.\d+)/));

      this.polly = new AWS.Polly();
      //Pull From Queue
      while(this._configQueue.length != 0){
        let config = this._configQueue.pop();
        this._cacheConfig(config);
      }

      // remove the listener once we've marked ourselves as ready
      SystemBus.removeListener(AwsSystem.SDK_READY_TO_START, this._onAwsSdkReadyToStart);
    };
    SystemBus.addListener(AwsSystem.SDK_READY_TO_START, this._onAwsSdkReadyToStart, true);
  }

  get ready() { return this._ready; }

  /**
   * Static method to get singleton instance
   * @return {SpeechManager}
   */
  static getInstance() {
    if(!theSpeechManager){
      theSpeechManager = new SpeechManager();
    }
    return theSpeechManager;
  }

  /**
   * Process the requested config or put in queue to be pick up later
   *
   * @param {SpeechManagerConfig} config
   */
  postConfig(config){
    config.body = this._getText(config.body);
    if(this.polly){
      this._cacheConfig(config);
    } else if (config.voiceId) {
      this._configQueue.push(config);
    }
  }

  /**
   * Get the audioBuffer and speechMarks for requested config - it will try to pull these from the cache if possible, otherwise
   * it will add a request to cache the config to the cache.
   * @param {SpeechManagerConfig} config
   * @return {Promise} resolves with the audioBuffer and speechMark
   */
  getAudioAndSpeechMark(config){
    config.body = this._getText(config.body);
    const audioBuffer = this._getAudioFromCache(config);
    const speechMark = this._getSpeechMarkFromCache(config);
    if(audioBuffer && speechMark){
      return Promise.resolve({audioBuffer: audioBuffer, speechMark: speechMark});
    }else{
      if(!this.polly){
        return Promise.reject(new Error('[Sumerian] AWS SDK is not available!'));
      }else{
        let request = this._getRequestFromCache(config);
        if(request){
          return request;
        }  else {
          // this is an uncached and unrequested config - cache it, then call ourselves again to retrieve the promise for that request
          this._cacheConfig(config);
          return this.getAudioAndSpeechMark(config);
        }
      }
    }
  }

  /**
   * Get audioBuffer from cache or return null if not found
   * @param {SpeechManagerConfig} config
   * @return {?AudioBuffer}
   */
  _getAudioFromCache(config){
    return ObjectUtils.get(this._speechCache, [config.voiceEngine, config.voiceId, config.body, 'audio'], null);
  }

  /**
   * Get speechMarks from cache or return null if not found
   * @param {SpeechManagerConfig} config
   * @return {?Object}
   */
  _getSpeechMarkFromCache(config){
    return ObjectUtils.get(this._speechCache, [config.voiceEngine, config.voiceId, config.body, 'speechMark'], null);
  }

  /**
   * Get polly request from cache or return null if not found
   * @param {SpeechManagerConfig} config
   * @return {?Promise}
   */
  _getRequestFromCache(config){
    return ObjectUtils.get(this._speechCache, [config.voiceEngine, config.voiceId, config.body, 'request'], null);
  }

  /**
   * get audioBuffer and speechMarks for requested config file via polly api
   *
   * @param {SpeechManagerConfig} config
   * @private
   */
  _cacheConfig(config){
    if (!config.voiceEngine) {
      config.voiceEngine = SpeechManager.POLLY_DEFAULTS.Engine;
    }
    else if (this._awsSdkVersion < SpeechManager.POLLY_ENGINE_SDK_VERSION && config.voiceEngine !== SpeechManager.POLLY_DEFAULTS.Engine) {
      config.voiceEngine = SpeechManager.POLLY_DEFAULTS.Engine;
      console.warn("[Sumerian] The version of the AWS SDK in use (" + this._awsSdkVersion + ") does not support the 'neural' engine parameter for AWS Polly API calls. Using the standard engine instead. Update to the latest AWS SDK version to enable this feature")
    }

    if(this._getRequestFromCache(config)){
      return;
    }

    if(!this._speechCache[config.voiceEngine]){
      this._speechCache[config.voiceEngine]= {};
    }
    if(!this._speechCache[config.voiceEngine][config.voiceId]){
      this._speechCache[config.voiceEngine][config.voiceId] = {};
    }
    if(!this._speechCache[config.voiceEngine][config.voiceId][config.body]){
      this._speechCache[config.voiceEngine][config.voiceId][config.body] = {};
    }

    let promises = [this._getAudio(config), this._getSpeechMarks(config)];
    this._speechCache[config.voiceEngine][config.voiceId][config.body]['request'] = Promise.all(promises).then(() => {
      this._cachedConfig.push(config);
      if(this._cachedConfig.length >= SpeechManager.maxCacheSize){
        const configToRemove = this._cachedConfig.shift();
        this._speechCache[configToRemove.voiceEngine][configToRemove.voiceId][configToRemove.body] = {};
      }
      const audioBuffer = this._getAudioFromCache(config);
      const speechMark = this._getSpeechMarkFromCache(config);
      return {audioBuffer: audioBuffer, speechMark: speechMark};
    });
  }

  /**
   * Returns the text content of the speech, ensuring it is wrapped in <speak></speak> tags.
   *
   * @returns {string}
   * @private
   */
  _getText(body) {
    return body.indexOf('<speak>') > -1
      ? body
      : `<speak>${body}</speak>`;
  }

  /**
   * Calls the polly api to synthesize the speech audio.
   * @param {Object} config
   * @returns {Promise}
   * @private
   */
  _getAudio(config) {
    const pollyConfig = Object.assign({}, SpeechManager.POLLY_DEFAULTS, {
      Text: config.body,
    });

    if (config.voiceId) {
      pollyConfig.VoiceId = config.voiceId;
    }

    if (config.voiceEngine) {
      pollyConfig.Engine = config.voiceEngine;
    }

    if (this._awsSdkVersion < SpeechManager.POLLY_ENGINE_SDK_VERSION) {
      delete pollyConfig["Engine"];
    }

    return this.polly
      .synthesizeSpeech(pollyConfig)
      .promise()
      .then(audio => {
        return this._processAudioStream(audio.AudioStream);
      })
      .then(buffer => {
        this._speechCache[config.voiceEngine][config.voiceId][config.body]['audio'] = buffer;
      })
      .catch(err => {
        throw err;
      });
  }

  /**
   * Converts the audio data returned from polly into audioBuffer
   *
   * @param {Array} audioStream The binary data of the mp3 returned from polly.
   * @returns {Promise}
   * @private
   */
  _processAudioStream(audioStream) {
    const audioArray = new Uint8Array(audioStream);
    const audioBuffer = audioArray.buffer;

    return new Promise(function(resolve,reject){
      const audioBufferCopy = audioBuffer.slice(0);
      AudioContext.getContext().decodeAudioData(audioBufferCopy, resolve, reject);
    });
  }

  /**
   * Calls the polly api to retrieve speech mark data.
   *
   * @param {Object} config
   * @returns {Promise}
   * @private
   */
  _getSpeechMarks(config) {
    const pollyConfig = Object.assign({}, SpeechManager.POLLY_SPEECH_MARKS_DEFAULTS, {
      Text: config.body,
    });

    if (config.voiceId) {
      pollyConfig.VoiceId = config.voiceId;
    }

    if (config.voiceEngine) {
      pollyConfig.Engine = config.voiceEngine;
    }

    if (this._awsSdkVersion < SpeechManager.POLLY_ENGINE_SDK_VERSION) {
      delete pollyConfig["Engine"];
    }

    return this.polly
      .synthesizeSpeech(pollyConfig)
      .promise()
      .then(data => {
        const jsonString = JSON.stringify(data.AudioStream);
        const json = JSON.parse(jsonString);
        this._speechCache[config.voiceEngine][config.voiceId][config.body]['speechMark'] = this._parseSpeechMarks(json.data);
      })
      .catch(err => {
        throw err;
      });
  }

  /**
   * Parses the speech mark data returned from polly api into a usable format.
   *
   * @param {Array} data
   * @returns {Object}
   * @private
   */
  _parseSpeechMarks(data) {
    let currentMark = '';
    const marks = {
      sentence: [],
      ssml: [],
      viseme: [],
      word: [],
    };

    for (const charCode of data) {
      const character = String.fromCharCode(charCode);
      let currentMarkData;

      currentMark += character;
      if (character === '}') {
        currentMarkData = JSON.parse(currentMark);
        currentMark = '';

        const type = currentMarkData.type;
        switch (type) {
          case 'sentence':
          case 'ssml':
          case 'viseme':
          case 'word':
            if (marks[type].length > 0) {
              this._addDurationToSpeechmark(
                marks[type][marks[type].length - 1],
                currentMarkData
              );
            }
            marks[type].push(currentMarkData);
            break;
          default:
            break;
        }
      }
    }

    return marks;
  }

  /**
   * Adds duration information to speech mark data.
   *
   * @param {Object} currentMark
   * @param {Object} nextMark
   * @private
   */
  _addDurationToSpeechmark(currentMark, nextMark) {
    currentMark.duration = nextMark.time - currentMark.time;
    currentMark.nextMark = nextMark.value;
  }
}

SpeechManager.POLLY_ENGINE_SDK_VERSION = 2.503;

// The defaults for polly api call to synthesize speech audio.
SpeechManager.POLLY_DEFAULTS = {
    Engine: 'standard',
    LexiconNames: [],
    OutputFormat: 'mp3',
    SampleRate: '22050',
    Text: '',
    TextType: 'ssml',
    VoiceId: 'Amy',
};

// The defaults for polly api call to synthesize speech mark data.
SpeechManager.POLLY_SPEECH_MARKS_DEFAULTS = {
    Engine: 'standard',
    LexiconNames: [],
    OutputFormat: 'json',
    SampleRate: '22050',
    SpeechMarkTypes: ['sentence', 'ssml', 'viseme', 'word'],
    Text: '',
    TextType: 'ssml',
    VoiceId: 'Amy',
};

//This is just a naive solution for now maybe we want a better way to manage cache. TODO: https://issues.amazon.com/SUMERIAN-2083
Object.defineProperty(SpeechManager, 'maxCacheSize' , {value: 100, writable : false, enumerable : true, configurable : false, });

export default SpeechManager;
